/**
 * Variant registry — the single source mapping each `VariantId` to its label,
 * default factory, conversion functions, and capabilities. Consumed by `model/`
 * (round-trip) and, later, PR-2's node palette.
 *
 * Two dispatch helpers (`variantDataFromDag`, `nodeDataToDag`) sit alongside the
 * registry object because a union-typed call through the registry index is not
 * type-safe for `toDag` (the data type cannot be correlated with the variant key
 * once destructured). The helpers recover that correlation without `any`.
 */
import type {
  BuilderDagFragment,
  BuilderNode,
  OpaqueKind,
  VariantData,
  VariantDataMap,
  VariantId,
  WireDagNode,
  WireVariantKey,
} from '../types';
import { VARIANT_CAPABILITIES, type VariantCapabilities } from './capabilities';
import { defaultLoopData, loopFromDag, loopToDag } from './loop';
import { approvalFromDag, approvalToDag, defaultApprovalData } from './approval';
import { cancelFromDag, cancelToDag, defaultCancelData } from './cancel';
import { defaultScriptData, scriptFromDag, scriptToDag } from './script';
import { commandFromDag, commandToDag, defaultCommandData } from './command';
import { defaultPromptData, promptFromDag, promptToDag } from './prompt';
import { bashFromDag, bashToDag, defaultBashData } from './bash';
import { defaultWaitData, waitFromDag, waitToDag } from './wait';

/** Canonical palette and registry order. */
export const VARIANTS: readonly VariantId[] = [
  'prompt',
  'command',
  'bash',
  'script',
  'loop',
  'approval',
  'wait',
  'cancel',
];

const VARIANT_SET: ReadonlySet<string> = new Set(VARIANTS);

/** Narrow an untrusted string to a `VariantId` (e.g. a drag-and-drop payload). */
export function isVariantId(value: string): value is VariantId {
  return VARIANT_SET.has(value);
}

/** A registry entry for one variant, typed against that variant's data shape. */
export interface VariantRegistryEntry<K extends VariantId> {
  label: string;
  defaultData: () => VariantDataMap[K];
  fromDag: (variantSpecific: Partial<WireDagNode>) => VariantDataMap[K];
  toDag: (data: VariantDataMap[K]) => BuilderDagFragment;
  capabilities: VariantCapabilities;
  /**
   * The wire keys this variant's converters consume from `variantSpecific`.
   * The importer warns about (and drops) any other key that lands there, so a
   * field the round-trip cannot carry is never lost silently. Typed as
   * `WireVariantKey` so a typo, a renamed wire field or a base key fails to
   * compile rather than silently classifying every key as unsupported.
   */
  wireKeys: readonly WireVariantKey[];
}

/** Per-variant registry. Strongly typed per key. */
export const VARIANT_REGISTRY: { [K in VariantId]: VariantRegistryEntry<K> } = {
  prompt: {
    label: 'Prompt',
    defaultData: defaultPromptData,
    fromDag: promptFromDag,
    toDag: promptToDag,
    wireKeys: ['prompt'],
    capabilities: VARIANT_CAPABILITIES.prompt,
  },
  command: {
    label: 'Command',
    defaultData: defaultCommandData,
    fromDag: commandFromDag,
    toDag: commandToDag,
    wireKeys: ['command', 'with'],
    capabilities: VARIANT_CAPABILITIES.command,
  },
  bash: {
    label: 'Bash',
    defaultData: defaultBashData,
    fromDag: bashFromDag,
    toDag: bashToDag,
    wireKeys: ['bash', 'timeout', 'on_timeout'],
    capabilities: VARIANT_CAPABILITIES.bash,
  },
  script: {
    label: 'Script',
    defaultData: defaultScriptData,
    fromDag: scriptFromDag,
    toDag: scriptToDag,
    wireKeys: ['script', 'runtime', 'deps', 'timeout', 'on_timeout', 'with'],
    capabilities: VARIANT_CAPABILITIES.script,
  },
  loop: {
    label: 'Loop',
    defaultData: defaultLoopData,
    fromDag: loopFromDag,
    toDag: loopToDag,
    wireKeys: ['loop', 'timeout'],
    capabilities: VARIANT_CAPABILITIES.loop,
  },
  approval: {
    label: 'Approval',
    defaultData: defaultApprovalData,
    fromDag: approvalFromDag,
    toDag: approvalToDag,
    wireKeys: ['approval'],
    capabilities: VARIANT_CAPABILITIES.approval,
  },
  wait: {
    label: 'Wait',
    defaultData: defaultWaitData,
    fromDag: waitFromDag,
    toDag: waitToDag,
    wireKeys: ['wait'],
    capabilities: VARIANT_CAPABILITIES.wait,
  },
  cancel: {
    label: 'Cancel',
    defaultData: defaultCancelData,
    fromDag: cancelFromDag,
    toDag: cancelToDag,
    wireKeys: ['cancel'],
    capabilities: VARIANT_CAPABILITIES.cancel,
  },
};

const OPAQUE_LABELS: Record<OpaqueKind, string> = {
  loop_group: 'Loop group',
  workflow: 'Workflow',
  include: 'Include',
};

const OPAQUE_CAPABILITIES: VariantCapabilities = { honorsAiFields: false };

/** Badge label for any builder node, read-only ones included. */
export function nodeLabel(node: BuilderNode): string {
  return node.variant === 'opaque'
    ? OPAQUE_LABELS[node.data.kind]
    : VARIANT_REGISTRY[node.variant].label;
}

/** Capability flags for any builder node, read-only ones included. */
export function nodeCapabilities(node: BuilderNode): VariantCapabilities {
  return node.variant === 'opaque'
    ? OPAQUE_CAPABILITIES
    : VARIANT_REGISTRY[node.variant].capabilities;
}

/**
 * Build the variant-specific data for a given variant from a partitioned wire
 * node. Safe through the registry index: every `fromDag` has the same parameter
 * type, so the union call resolves cleanly.
 */
export function variantDataFromDag(
  variant: VariantId,
  variantSpecific: Partial<WireDagNode>
): VariantData {
  return VARIANT_REGISTRY[variant].fromDag(variantSpecific);
}

/**
 * Serialize a builder node's variant data to its sparse wire fragment. A switch
 * on the discriminant lets `node.data` narrow to the matching `toDag` parameter
 * type — no casts, no `any`.
 */
export function nodeDataToDag(node: BuilderNode): BuilderDagFragment {
  switch (node.variant) {
    case 'opaque':
      return node.data.fields;
    case 'loop':
      return loopToDag(node.data);
    case 'approval':
      return approvalToDag(node.data);
    case 'wait':
      return waitToDag(node.data);
    case 'cancel':
      return cancelToDag(node.data);
    case 'script':
      return scriptToDag(node.data);
    case 'command':
      return commandToDag(node.data);
    case 'prompt':
      return promptToDag(node.data);
    case 'bash':
      return bashToDag(node.data);
  }
}
